// Route: /audio-recaps/[id] — студийный layout аудио-резюме:
//   - desktop ≥1024px: транскрипт (flex-1) + RecapPlayerBar sticky снизу.
//   - mobile <1024px:  транскрипт + компактный RecapPlayerBar.
//
// Редизайн (visual-audio-tutor): встроенный «Чат по аудио-резюме» убран.
// Вопросы делегированы глобальному AI-репетитору (правая боковая панель,
// useSidePanelStore) — контекст recap'а доклеивается автоматически хуком
// usePageContext. В шапке — кнопка «Спросить репетитора»; на каждой реплике
// транскрипта — «Спросить про этот отрывок» (см. script.tsx).
//
// Тактильный редизайн «Картотека»: тактильный header с category-плиткой,
// фазовый прогресс-список генерации, единые ErrorState.
//
// См. docs/ui-spec/audio-recaps.[id].{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import {
	Button,
	CategoryTile,
	ElevatedPanel,
	EntityDetailHeader,
	ErrorState,
	FORM_PAGE_SHELL,
	GenerationProgressRing,
	NARROW_PAGE_SHELL,
	PANEL_PAD,
	Skeleton,
} from "@studyhub/ui";
import { ScrollArea } from "@studyhub/ui/components/scroll-area";
import { IconTooltip } from "@studyhub/ui/components/tooltip";
import { toast } from "@studyhub/ui/toast";
import {
	IconArrowLeft,
	IconCheck,
	IconDotsVertical,
	IconDownload,
	IconHeadphones,
	IconRefresh,
	IconShare,
} from "@tabler/icons-react";
import { createFileRoute, Link, Navigate, useParams, useSearch } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type AudioRecapStatus, useAudioRecap } from "@/components/audio-recaps/_api";
import { triggerRecapDownload } from "@/components/audio-recaps/_download";
import {
	audioRecapGenerationTick,
	audioRecapSocketTick,
} from "@/components/audio-recaps/generation-tick";
import { RecapPlayerBar } from "@/components/audio-recaps/recap-player-bar";
import { Script } from "@/components/audio-recaps/script";
import { ShareLinkDialog } from "@/components/audio-recaps/share-link-dialog";
import { ActionMenu, type ActionMenuItem } from "@/components/shared/action-menu";
import { OfflineDetailState } from "@/components/shared/offline-detail-state";
import { AskTutorHeaderButton } from "@/components/tutor/ask-tutor-header-button";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { useGenerationProgress } from "@/hooks/use-generation-progress";
import { markArtifactSeenKey, useMarkArtifactSeen } from "@/hooks/use-mark-artifact-seen";
import { useRealtime } from "@/hooks/use-realtime";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { useTier } from "@/hooks/use-tier";
import { useTrackView } from "@/hooks/use-track-view";
import { fromSearchSchema, resolveBackFrom } from "@/lib/back-from";
import { cn } from "@/lib/cn";
import { formatDuration } from "@/lib/format";
import { isOfflineWithoutCache } from "@/lib/offline-query-state";
import { useIsMobilePlatform } from "@/lib/platform-detect";
import { api } from "@/lib/trpc-client";
import { useAudioRecapStore } from "@/stores/audio-recap";

const searchSchema = z
	.object({
		demo: z.enum(["loading", "ready", "processing", "error"]).optional(),
	})
	.merge(fromSearchSchema);

export const Route = createFileRoute("/audio-recaps/$id")({
	validateSearch: tolerantSearch(searchSchema),
	component: AudioRecapDetailPage,
	pendingComponent: DetailPendingFallback,
});

// Route-level pending skeleton — повторяет bounded студийный layout (header +
// контент + sticky player bar), чтобы переход был мгновенным без layout shift.

/**
 * Текст под код провала генерации. Незнакомый код (или его отсутствие) даёт
 * прежнюю общую формулировку — новый код на сервере не обязан ждать релиза
 * фронта, чтобы экран остался осмысленным.
 */
function failReasonText(
	t: TFunction<"audio">,
	code: string | null,
	part: "title" | "hint",
): string {
	const generic = part === "title" ? "detail.generateFailedTitle" : "detail.generateFailedHint";
	if (!code) return t(generic);
	const key = `detail.failReason.${code}.${part}`;
	const text = t(key, { defaultValue: "" });
	return text.length > 0 ? text : t(generic);
}

function DetailPendingFallback(): React.ReactElement {
	return (
		<div className="flex h-fit-app min-h-0 flex-col overflow-hidden">
			<div className="flex items-start gap-3 border-b border-border px-4 pb-3 pt-4 lg:px-6">
				<Skeleton className="size-10 rounded-[var(--radius-tile)]" />
				<div className="min-w-0 flex-1 space-y-2">
					<Skeleton className="h-5 w-2/3" />
					<Skeleton className="h-3 w-40" />
				</div>
				<Skeleton className="size-9 shrink-0 rounded-full" />
			</div>
			<div className="min-h-0 flex-1 space-y-3 px-4 py-4 lg:px-6">
				<Skeleton className="h-24 w-full rounded-[var(--radius-card)]" />
				<Skeleton className="h-24 w-full rounded-[var(--radius-card)]" />
				<Skeleton className="h-24 w-full rounded-[var(--radius-card)]" />
			</div>
			<div className="shrink-0 border-t border-border px-4 py-3">
				<Skeleton className="h-11 w-full rounded-[var(--radius-md)]" />
			</div>
		</div>
	);
}

type GeneratingStatus = Exclude<AudioRecapStatus, "ready" | "error">;

const GEN_ORDER: ReadonlyArray<GeneratingStatus> = [
	"queued",
	"generating-script",
	"generating-tts",
];

// ───────── generating state ─────────

function GeneratingState({
	status,
	recapId,
	stageStartedAt,
	targetDurationSec,
}: {
	status: GeneratingStatus;
	recapId: string;
	/** ISO-время начала стадии на сервере — пол кольца переживает перезагрузку. */
	stageStartedAt: string | null;
	targetDurationSec: number | null;
}): React.ReactElement {
	const { t } = useTranslation("audio");
	const { t: tc } = useTranslation("common");
	const currentIdx = GEN_ORDER.indexOf(status);
	const { progress, percent, etaMsRemaining, applyTick, complete, reset } = useGenerationProgress({
		defaultCrawlMs: 28_000,
		crawlCap: 0.9,
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset on recap switch
	useEffect(() => {
		reset();
	}, [recapId, reset]);

	// TTS длинного подкаста — ОДИН вызов Gemini без промежуточных socket-тиков,
	// поэтому пол кольца считается от времени стадии на сервере (то же правило,
	// что в сцене темы курса — `components/audio-recaps/generation-tick.ts`).
	const [progressNow, setProgressNow] = useState(() => Date.now());
	useEffect(() => {
		const id = window.setInterval(() => setProgressNow(Date.now()), 3_000);
		return () => window.clearInterval(id);
	}, []);

	useEffect(() => {
		const tick = audioRecapGenerationTick(status, {
			stageStartedAt,
			targetDurationSec,
			now: progressNow,
		});
		if (tick) applyTick(tick);
	}, [status, stageStartedAt, targetDurationSec, progressNow, applyTick]);

	useSocketEvent("audio-recap:tts_progress", (payload) => {
		if (payload.recapId !== recapId) return;
		applyTick(
			audioRecapSocketTick({
				progress: payload.progress,
				...(payload.etaMsRemaining ? { etaMsRemaining: payload.etaMsRemaining } : {}),
			}),
		);
	});

	useSocketEvent("audio-recap:ready", (payload) => {
		if (payload.recapId !== recapId) return;
		complete();
	});

	const etaPrefix = tc("generation.etaApprox", { defaultValue: "~" });
	const etaLabel =
		etaMsRemaining != null && etaMsRemaining > 0
			? (() => {
					const sec = Math.ceil(etaMsRemaining / 1000);
					return sec < 60
						? tc("generation.etaSeconds", {
								prefix: etaPrefix,
								count: sec,
								defaultValue: "{prefix}{count}s",
							})
						: tc("generation.etaMinutes", {
								prefix: etaPrefix,
								count: Math.ceil(sec / 60),
								defaultValue: "{prefix}{count}m",
							});
				})()
			: null;
	const progressLabel = tc("generation.progress", {
		percent,
		defaultValue: `${percent}%`,
	});

	return (
		<ElevatedPanel className={cn(PANEL_PAD, "space-y-5")}>
			<div className="flex flex-col items-center gap-3 text-center">
				<GenerationProgressRing progress={progress} size={56} label={progressLabel} />
				<div className="space-y-1">
					<h2 className="text-base font-semibold text-foreground">
						{t(`detail.gen.${status}.title`)}
					</h2>
					<p className="text-sm text-muted-foreground">{t(`detail.gen.${status}.hint`)}</p>
					{etaLabel ? (
						<p className="text-xs tabular-nums text-muted-foreground">{etaLabel}</p>
					) : null}
				</div>
			</div>

			<ol className="space-y-2">
				{GEN_ORDER.map((step, idx) => {
					const isPast = idx < currentIdx;
					const isActive = idx === currentIdx;
					return (
						<li
							key={step}
							className={cn(
								"flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5",
								isActive ? "bg-primary/5" : "bg-transparent",
							)}
						>
							<span className="relative inline-flex size-7 shrink-0 items-center justify-center">
								{/* Кольцо и заливка — КРУГИ. Со скруглённым квадратом
								    (`rounded-[10px]`) вращение читалось как «квадратное
								    колесо»: у squircle радиус меняется по периметру, и
								    при spin силуэт пульсирует. Зазор — `inset-[-4px]`. */}
								{isActive ? (
									<span
										className="absolute inset-[-4px] animate-spin rounded-full border-2 border-primary/30 border-t-primary motion-reduce:animate-none"
										aria-hidden
									/>
								) : null}
								<span
									className={cn(
										"relative inline-flex size-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums",
										"[box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]",
										isPast && "bg-[var(--c-mastery-bg)] text-[var(--c-mastery-fg)]",
										isActive &&
											"text-[var(--sh-primary-fg)] [background-image:linear-gradient(180deg,var(--primary-2),var(--primary))]",
										!isPast && !isActive && "sh-inset-field text-muted-foreground",
									)}
								>
									{isPast ? <IconCheck size={14} /> : idx + 1}
								</span>
							</span>
							<span
								className={cn(
									"min-w-0 flex-1 text-sm",
									isActive
										? "font-medium text-foreground"
										: isPast
											? "text-muted-foreground"
											: "text-muted-foreground/70",
								)}
							>
								{t(`detail.gen.${step}.title`)}
							</span>
						</li>
					);
				})}
			</ol>

			<p className="text-center text-xs tabular-nums text-muted-foreground">
				{t("detail.gen.autoRefresh")}
			</p>
		</ElevatedPanel>
	);
}

function AudioRecapDetailPage(): React.ReactElement {
	const { t } = useTranslation("audio");
	const { id } = useParams({ from: "/audio-recaps/$id" });
	useTrackView("audio-recap", id);
	const search = useSearch({ from: "/audio-recaps/$id" });
	const { isFree } = useTier();
	const query = useAudioRecap(id);
	const isMobile = useIsMobilePlatform();
	const materialId = query.data?.materialId ?? null;
	useArtifactWatching(
		[
			markArtifactSeenKey("audio-recap", id),
			materialId ? markArtifactSeenKey("material", materialId) : null,
		].filter((k): k is string => Boolean(k)),
	);

	const setRecap = useAudioRecapStore((s) => s.setRecap);
	const pause = useAudioRecapStore((s) => s.pause);
	const [shareOpen, setShareOpen] = useState(false);

	useEffect(() => {
		const data = query.data;
		if (!data || data.status !== "ready") return;
		const current = useAudioRecapStore.getState().recapId;
		if (current === data.id) return;
		setRecap({ recapId: data.id, title: data.title, durationSec: data.durationSec });
		pause();
	}, [query.data, setRecap, pause]);

	// Мини-плеер на других страницах — только если ушли с detail во время
	// playback. Пауза / конец трека → закрываем store, иначе sticky bar
	// всплывает на dashboard/tutor и т.п.
	useEffect(() => {
		return () => {
			const state = useAudioRecapStore.getState();
			if (state.recapId === id && !state.isPlaying) {
				state.close();
			}
		};
	}, [id]);

	const isGenerating =
		query.data &&
		(query.data.status === "queued" ||
			query.data.status === "generating-script" ||
			query.data.status === "generating-tts");
	useEffect(() => {
		if (!isGenerating) return;
		const t2 = setInterval(() => void query.refetch(), 3000);
		return () => clearInterval(t2);
	}, [isGenerating, query.refetch]);

	const utilsForRealtime = api.useUtils();
	const markSeen = useMarkArtifactSeen();
	// Surface-ack не только по socket: polling часто ловит ready раньше/вместо
	// `audio-recap:ready` → без этого колокол + email/push не гасятся.
	useEffect(() => {
		if (query.data?.status !== "ready") return;
		markSeen(markArtifactSeenKey("audio-recap", id), "surface");
	}, [query.data?.status, id, markSeen]);
	useRealtime({
		channel: "audio-recap:ready",
		onEvent: (rawPayload) => {
			void utilsForRealtime.audioRecaps.recaps.getById.invalidate({ recapId: id });
			// Surface-ack: пользователь на странице этого аудио-резюме (видим) →
			// гасим email/push + колокол (presence-aware §3.3, матрица 4.1).
			// scope по recapId === route id.
			const payload = rawPayload as { recapId: string };
			if (payload.recapId === id) {
				markSeen(markArtifactSeenKey("audio-recap", id), "surface");
			}
		},
	});
	useRealtime({
		channel: "audio-recap:script_ready",
		onEvent: () => void utilsForRealtime.audioRecaps.recaps.getById.invalidate({ recapId: id }),
	});

	const isLoading = search.demo === "loading" || query.isLoading;
	const isError = search.demo === "error" || query.isError;
	const recap = query.data;

	const utils = api.useUtils();

	// Перегенерация упавшего recap'а с теми же настройками (in-place). После успеха
	// статус → queued, страница сама перейдёт в GeneratingState (polling/realtime).
	const regenerateMutation = api.audioRecaps.recaps.regenerate.useMutation({
		onSuccess: () => {
			void utils.audioRecaps.recaps.getById.invalidate({ recapId: id });
			toast.success(
				t("wizard.toast.regenerateStarted", { defaultValue: "Перегенерация запущена" }),
			);
		},
		onError: () => toast.error(t("wizard.toast.regenerateFailed", { defaultValue: "Не удалось" })),
	});

	const backButton = useMemo(() => {
		const backOrigin = resolveBackFrom(search.from);
		const cls = "-ml-2 h-7 px-2 text-muted-foreground";
		if (backOrigin?.kind === "material") {
			return (
				<Button variant="ghost" size="sm" asChild className={cls}>
					<Link to="/materials/$materialId" params={{ materialId: backOrigin.materialId }}>
						<IconArrowLeft size={15} />
						{t("detail.backMaterial", { defaultValue: "Материал" })}
					</Link>
				</Button>
			);
		}
		if (backOrigin?.kind === "subsection") {
			return (
				<Button variant="ghost" size="sm" asChild className={cls}>
					<Link
						to="/study-plan/$id/subsection/$subId"
						params={{ id: backOrigin.planId, subId: backOrigin.subsectionId }}
					>
						<IconArrowLeft size={15} />
						{t("detail.backSubsection", { defaultValue: "К подсекции" })}
					</Link>
				</Button>
			);
		}
		return (
			<Button variant="ghost" size="sm" asChild className={cls}>
				<Link to="/audio-recaps">
					<IconArrowLeft size={15} />
					{t("detail.backAll", { defaultValue: "Все аудио" })}
				</Link>
			</Button>
		);
	}, [search.from, t]);

	// Промо-период: все юзеры Pro, гард не срабатывает; на всякий случай ведём
	// не на отключённый /billing/checkout, а на /settings/billing.
	if (isFree) {
		return <Navigate to="/settings/billing" replace />;
	}

	const handleDownload = async (): Promise<void> => {
		if (!recap || recap.status !== "ready") return;
		try {
			const { url } = await utils.audioRecaps.recaps.getDownloadUrl.fetch({
				recapId: recap.id,
			} as never);
			triggerRecapDownload(url, recap.title);
		} catch {
			toast.error(t("detail.downloadFailed"));
		}
	};

	// Loading — простой одноколоночный экран.
	if (isLoading) {
		return (
			<div className={cn(NARROW_PAGE_SHELL, "space-y-section")}>
				<div>{backButton}</div>
				<Skeleton className="h-9 w-2/3" />
				<Skeleton className="h-64 w-full rounded-[var(--radius-panel)]" />
			</div>
		);
	}
	if (isError) {
		return (
			<div className={cn(NARROW_PAGE_SHELL, "space-y-section")}>
				<div>{backButton}</div>
				<ErrorState
					title={t("detail.loadFailed")}
					action={
						<Button onClick={() => void query.refetch()} variant="outline" size="sm">
							{t("detail.retry")}
						</Button>
					}
				/>
			</div>
		);
	}
	// Офлайн без локальной копии: запрос paused (не loading и не error), поэтому
	// раньше здесь рендерился пустой <div /> — белый экран без объяснений.
	if (isOfflineWithoutCache(query)) {
		return (
			<div className={cn(NARROW_PAGE_SHELL, "space-y-section")}>
				<div>{backButton}</div>
				<OfflineDetailState onRetry={() => void query.refetch()} />
			</div>
		);
	}
	if (!recap) return <div />;

	// Recap-status: error и generating — однопанельные.
	if (recap.status !== "ready") {
		return (
			<div className={cn(NARROW_PAGE_SHELL, "space-y-section")}>
				<div>{backButton}</div>
				<header className="flex items-start gap-3">
					<CategoryTile category="ai" icon={<IconHeadphones />} size="lg" />
					<div className="min-w-0 space-y-1">
						{/* line-clamp-2 (не truncate): длинное название recap'а — основной
						    идентификатор, на узком экране важно видеть его целиком (2 строки). */}
						<h1 className="line-clamp-2 text-2xl font-semibold tracking-tight">{recap.title}</h1>
						<div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
							<span>{t(`formats.${recap.format}`)}</span>
							<span aria-hidden className="text-border">
								·
							</span>
							<span>{t(`voices.${recap.voiceMode}`)}</span>
						</div>
					</div>
				</header>
				{recap.status === "error" ? (
					<ErrorState
						// Причина отказа, если сервер её назвал. Раньше экран всегда
						// показывал «Не удалось сгенерировать аудио» и предлагал
						// перегенерировать — на устранимой причине («в материале
						// почти нет текста») это отправляло человека жать кнопку по
						// кругу с тем же исходом. `errorMessage` = стабильный код из
						// `sanitizeAiErrorForClient`, не текст модели.
						title={failReasonText(t, recap.errorMessage, "title")}
						description={failReasonText(t, recap.errorMessage, "hint")}
						action={
							<div className="flex flex-wrap items-center justify-center gap-2">
								{/* Основное действие — перегенерировать те же настройки in-place. */}
								<Button
									onClick={() => regenerateMutation.mutate({ recapId: recap.id })}
									disabled={regenerateMutation.isPending}
									className="cursor-pointer"
								>
									<IconRefresh size={16} />
									{t("detail.regenerate", { defaultValue: "Перегенерировать" })}
								</Button>
								<Button variant="outline" asChild className="cursor-pointer">
									<Link to="/audio-recaps/new">{t("detail.createNew")}</Link>
								</Button>
							</div>
						}
					/>
				) : (
					<GeneratingState
						status={recap.status}
						recapId={recap.id}
						stageStartedAt={recap.status === "generating-tts" ? recap.updatedAt : recap.createdAt}
						targetDurationSec={recap.targetDurationSec}
					/>
				)}
			</div>
		);
	}

	// ── Status = ready — студийный layout ───────────────────────────────
	// Порог 75% совпадает с серверным degradation-логом
	// (`audio-recap-duration-shortfall` в inngest/functions/audioSummary.ts):
	// ниже него результат уже не «немного короче», а заметно другой.
	const isDurationShortfall =
		recap.targetDurationSec > 0 &&
		recap.durationSec > 0 &&
		recap.durationSec < recap.targetDurationSec * 0.75;

	const headerActionItems: ActionMenuItem[] = [
		{
			key: "download",
			label: t("detail.downloadAria"),
			icon: <IconDownload />,
			onSelect: () => void handleDownload(),
		},
		{
			key: "share",
			label: t("detail.shareAria"),
			icon: <IconShare />,
			onSelect: () => setShareOpen(true),
		},
	];

	const header = (
		<header className="space-y-3 border-b border-border px-4 pb-3 pt-4 lg:px-6">
			<div>{backButton}</div>
			{/* items-start внутри EntityDetailHeader: при переносе названия на 2 строки
			    плитка остаётся у верхней строки. line-clamp-2 — название recap'а на
			    mobile важно видеть целиком (основной идентификатор страницы). */}
			{/* title-first: back уже сверху — actions не должны вставать между back и названием. */}
			<EntityDetailHeader
				stackOrder="title-first"
				leading={<CategoryTile category="ai" icon={<IconHeadphones />} size="md" />}
				title={<h1 className="line-clamp-2 text-xl font-semibold tracking-tight">{recap.title}</h1>}
				meta={
					<div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground sm:text-2xs">
						<span>{t(`formats.${recap.format}`)}</span>
						<span aria-hidden className="text-border">
							·
						</span>
						<span>{t(`voices.${recap.voiceMode}`)}</span>
						{/* Честная пометка о недоборе длительности: раньше расхождение
						    «заказал 10 минут — получил 2:37» не объяснялось нигде
						    (тихая деградация, rules/logging.md §13a-13c). */}
						{isDurationShortfall ? (
							<>
								<span aria-hidden className="text-border">
									·
								</span>
								<span
									className="rounded-sm bg-warning/10 px-1.5 py-0.5 font-medium text-warning"
									title={t("detail.durationShortfallHint", {
										target: Math.round(recap.targetDurationSec / 60),
										actual: formatDuration(recap.durationSec),
									})}
								>
									{t("detail.durationShortfall")}
								</span>
							</>
						) : null}
					</div>
				}
				actions={
					<>
						{isMobile ? (
							// На mobile download+share объединены в одно action-меню (нижняя
							// шторка с крупными строками) — согласовано с карточкой recap.
							<ActionMenu
								title={t("card.actions", { defaultValue: "Действия" })}
								items={headerActionItems}
								trigger={
									<Button
										variant="ghost"
										size="icon"
										aria-label={t("card.actions", { defaultValue: "Действия" })}
									>
										<IconDotsVertical size={18} />
									</Button>
								}
							/>
						) : (
							<>
								<IconTooltip label={t("detail.downloadAria")}>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => void handleDownload()}
										aria-label={t("detail.downloadAria")}
									>
										<IconDownload size={18} />
									</Button>
								</IconTooltip>
								<IconTooltip label={t("detail.shareAria")}>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => setShareOpen(true)}
										aria-label={t("detail.shareAria")}
									>
										<IconShare size={18} />
									</Button>
								</IconTooltip>
							</>
						)}
						{/* «Спросить репетитора» — открывает глобальную боковую панель
						    AI-репетитора; контекст recap'а доклеится автоматически.
						    На mobile — иконка (узкая шапка), на desktop — с подписью. */}
						<AskTutorHeaderButton ns="audio" labelKey="chat.askTutor" />
					</>
				}
			/>
		</header>
	);

	const sharedDialogs = (
		<ShareLinkDialog open={shareOpen} onOpenChange={setShareOpen} recapId={recap.id} />
	);

	// Единый layout (desktop + mobile): транскрипт во всю ширину + sticky
	// player bar снизу. Чат-панель убрана — вопросы идут в глобального
	// репетитора (кнопка в шапке + кнопка на каждой реплике в транскрипте).
	// h-fit-app — единый helper высоты (topbar+banners+bottom-nav+safe-area+
	// audio-player), чтобы RecapPlayerBar не уезжал под home-indicator/bottom-nav.
	return (
		<div className="flex h-fit-app min-h-0 flex-col overflow-hidden">
			{header}
			<ScrollArea className="min-h-0 flex-1">
				<div className={FORM_PAGE_SHELL}>
					<Script />
				</div>
			</ScrollArea>
			<RecapPlayerBar />
			{sharedDialogs}
		</div>
	);
}
